import {gql} from "@apollo/client";

import {MAX_VISIBLE_CHECKS} from "@/lib/constants/maintenance.ts";
import {GraphQLResponse} from "@/lib/types/common.ts";
import {
  MaintenanceAuditEventCheck,
  MaintenanceEvent,
  MaintenanceOccurrence,
  MaintenanceSchedule,
  MaintenanceScheduleListItem,
  MaintenanceScheduleWithServices,
} from "@/lib/types/maintenance.ts";

export interface MaintenanceListResponse {
  response: MaintenanceListResponseValues;
}

export interface MaintenanceListResponseValues {
  total: number;
  items: MaintenanceScheduleListItem[];
}

export interface ActiveOccurrencesListResponse {
  response: ActiveOccurrencesListResponseValues;
}

export interface ActiveOccurrencesListResponseValues {
  total: number;
  items: MaintenanceOccurrence[];
}

export interface MaintenanceAuditEventsPageResult {
  maintenanceAuditEvents: {
    items: MaintenanceEvent[];
    total: number;
  };
}

export interface MaintenanceAuditEventChecksPageResult {
  maintenanceAuditEventChecks: {
    items: MaintenanceAuditEventCheck[];
    total: number;
  };
}

export const GQL_MAINTENANCE_AUDIT_EVENTS_QUERY = gql`
  query MaintenanceAuditEvents(
    $offset: Int
    $limit: Int
    $sort: String
    $search: String
    $filters: MaintenanceAuditEventFilters
  ) {
    maintenanceAuditEvents(offset: $offset, limit: $limit, sort: $sort, search: $search, filters: $filters) {
      total
      items {
        id
        created_at
        event_type
        services_at_creation {
          id
        }
        service {
          id
          address
          name
          type
        }
        schedule {
          id
          name
          rrule
          duration_minutes
          schedule_type
          starts_at
          ends_at
          is_active
          pause_checks_during_maintenance
          services {
            id
            address
            name
            type
          }
        }
        occurrence {
          id
          starts_at
          ends_at
          services {
            id
            address
            name
            type
          }
        }
        metadata
        audit_log_entry {
          id
          created_at
          user_display
          model_display
          object_str
          object_id
          comment
          changes_list {
            class
            verbose_name
            to_many
            added {
              id
              verbose
            }
            removed {
              id
              verbose
            }
            new {
              value
              verbose
            }
            old {
              value
              verbose
            }
          }
        }
      }
    }
  }
`;

export const GQL_MAINTENANCE_AUDIT_EVENT_CHECKS = gql`
  query MaintenanceAuditEventChecks(
    $audit_event_id: Int!
    $offset: Int!
    $limit: Int!
    $search: String
    $sort: String
  ) {
    maintenanceAuditEventChecks(
      audit_event_id: $audit_event_id
      offset: $offset
      limit: $limit
      search: $search
      sort: $sort
    ) {
      total
      items {
        id
        name
        monitoring_service_type
        is_deleted
      }
    }
  }
`;

export const GQL_MAINTENANCE_SCHEDULES_FILTER_LIST = gql`
  query ListMaintenanceSchedulesFilter($search: String, $limit: Int) {
    values: maintenanceSchedules(search: $search, limit: $limit) {
      total
      items {
        key: id
        display: name
      }
    }
  }
`;

export const GQL_MAINTENANCE_SCHEDULES_LIST = gql`
  query ListMaintenanceSchedules(
    $offset: Int
    $limit: Int
    $sort: String
    $search: String
    $filters: MaintenanceScheduleFilters
    $previewLimit: Int = ${MAX_VISIBLE_CHECKS}
  ) {
    response: maintenanceSchedules(
      offset: $offset
      limit: $limit
      sort: $sort
      search: $search
      filters: $filters
      preview_limit: $previewLimit
    ) {
      total
      items {
        id
        name
        schedule_type
        rrule
        duration_minutes
        is_active
        starts_at
        ends_at
        pause_checks_during_maintenance
        tags {
          id
          tag
          color_hex
        }
        services_count
        services_preview {
          id
          address
          name
          type
        }
        deleted_at
        created_at
        modified_at
        is_in_maintenance
        next_occurrence {
          id
          starts_at
          ends_at
        }
      }
    }
  }
`;

export interface MaintenanceScheduleServiceCountsResponse {
  counts: {
    service_id: number;
    count: number;
  }[];
}

// Returns, per check, how many schedules it belongs to (excluding the schedule being edited) via a
// single server-side aggregate — so the "in N schedules" chips need one request for the whole
// selection instead of one per check.
export const GQL_MAINTENANCE_SCHEDULE_SERVICE_COUNTS = gql`
  query MaintenanceScheduleServiceCounts($serviceIds: [Int!]!, $excludeScheduleId: Int) {
    counts: maintenanceScheduleServiceCounts(service_ids: $serviceIds, exclude_schedule_id: $excludeScheduleId) {
      service_id
      count
    }
  }
`;

export type CreateMaintenanceScheduleResult = GraphQLResponse<MaintenanceSchedule>;

export type EditMaintenanceScheduleResult = GraphQLResponse<MaintenanceSchedule>;

export interface GetMaintenanceScheduleResult {
  maintenanceSchedule: MaintenanceScheduleWithServices;
}

export const GQL_MAINTENANCE_SCHEDULE_GET = gql`
  query GetMaintenanceSchedule($id: ID!) {
    maintenanceSchedule(id: $id) {
      id
      name
      schedule_type
      rrule
      duration_minutes
      is_active
      starts_at
      ends_at
      pause_checks_during_maintenance
      tags {
        id
        tag
        color_hex
      }
      services {
        id
        address
        name
        type
      }
      notifications {
        id
        offset
        event
        contact_groups {
          id
          name
        }
      }
      deleted_at
      created_at
      modified_at
    }
  }
`;

export const GQL_MAINTENANCE_SCHEDULE_CREATE = gql`
  mutation CreateMaintenanceSchedule($data: MaintenanceScheduleInput!) {
    response: createMaintenanceSchedule(data: $data) {
      ... on CreateMaintenanceScheduleSuccess {
        status
        data {
          id
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_MAINTENANCE_SCHEDULE_EDIT = gql`
  mutation EditMaintenanceSchedule($id: Int!, $data: MaintenanceScheduleUpdateInput!) {
    response: editMaintenanceSchedule(id: $id, data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_MAINTENANCE_SERVICES_FILTER_LIST = gql`
  query ListServices($search: String, $limit: Int) {
    values: services(search: $search, limit: $limit) {
      total
      items {
        key: id
        display: name
        device {
          address
        }
      }
    }
  }
`;

export const GQL_MAINTENANCE_TAGS_FILTER_LIST = gql`
  query ListServices($search: String, $limit: Int) {
    values: serviceTags(limit: $limit, search: $search) {
      items {
        key: id
        display: tag
        color_hex
      }
      total
    }
  }
`;

export const GQL_MAINTENANCE_SCHEDULE_DELETE = gql`
  mutation DeleteMaintenanceSchedule($id: Int!) {
    response: deleteMaintenanceSchedule(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_UNLINK_SERVICE_FROM_MAINTENANCE_SCHEDULE = gql`
  mutation UnlinkServiceFromMaintenanceSchedule($scheduleId: Int!, $serviceId: Int!) {
    response: unlinkServiceFromMaintenanceSchedule(scheduleId: $scheduleId, serviceId: $serviceId) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_MAINTENANCE_OCCURRENCES_LIST = gql`
  query ListMaintenanceScheduleOccurrences(
    $offset: Int
    $limit: Int
    $sort: String
    $search: String
    $filters: MaintenanceScheduleOccurrenceFilters
  ) {
    response: maintenanceOccurrences(offset: $offset, limit: $limit, sort: $sort, search: $search, filters: $filters) {
      total
      items {
        id
        created_at
        modified_at
        schedule {
          id
          name
          schedule_type
          rrule
          duration_minutes
          is_active
          starts_at
          ends_at
          pause_checks_during_maintenance
          tags {
            id
            tag
            color_hex
          }
          services {
            id
            address
            name
            type
          }
          deleted_at
          created_at
          modified_at
          next_occurrence {
            id
            starts_at
            ends_at
          }
        }
        starts_at
        ends_at
        services {
          id
          address
          name
          type
        }
        recurrence_id
        is_exception
        exception_reason
        cancelled_at
        pause_checks_during_maintenance
      }
    }
  }
`;

export const GQL_END_ACTIVE_MAINTENANCE_WINDOW = gql`
  mutation EndActiveMaintenanceWindow($occurrenceId: Int!) {
    response: endActiveMaintenanceWindow(occurrenceId: $occurrenceId) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_BULK_END_ACTIVE_MAINTENANCE_WINDOWS = gql`
  mutation BulkEndActiveMaintenanceWindows($occurrenceIds: [Int!]!) {
    response: bulkEndActiveMaintenanceWindows(occurrenceIds: $occurrenceIds) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_BULK_DELETE_MAINTENANCE_SCHEDULES = gql`
  mutation BulkDeleteMaintenanceSchedules($ids: [Int!]!) {
    response: bulkDeleteMaintenanceSchedules(ids: $ids) {
      ... on GenericMutationResponseSuccess {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;
